'use strict';

import React, {
    Ref,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {useSelector} from 'react-redux';
import parse from 'html-react-parser';
import _ from 'lodash';
import {keys} from 'ts-transformer-keys';

import {DomHandler} from 'primereact/utils';
import {Button} from 'primereact/button';
import {
    FileUpload,
    FileUploadHeaderTemplateOptions,
    FileUploadSelectEvent
} from 'primereact/fileupload';
import {
    InputTextarea,
    InputTextareaProps
} from 'primereact/inputtextarea';
import {OverlayPanel} from 'primereact/overlaypanel';
import {OverlayService} from 'primereact/overlayservice';
import {useOverlayScrollListener} from 'primereact/hooks';

import {__} from '~/shared/src/utilities/messageCatalog';

import FieldWrapper, {
    DefaultProps as FieldWrapperDefaultProps,
    defaultProps as fieldWrapperDefaultProps,
    Props as FieldWrapperProps,
    ComponentProps as FieldWrapperComponentProps,
    componentProps as fieldWrapperComponentProps
} from '~/frontend/src/components/base/shared/field/FieldWrapper';

import {StoreState} from '~/frontend/src/store';
import {getHeightStyle} from '~/frontend/src/components/base/shared/field/valueTypeFields/TextField';
import {imageRenderer} from '~/frontend/src/utilities/valueRenderers';
import {formatBytes} from '~/shared/src/utilities/valueFormaters';
import {useHookFormDelay} from '~/shared/src/utilities/modules/form';
import {useGlobalClickListener} from '~/frontend/src/components/base/page/PageTabPanel';


type InputProps = Omit<InputTextareaProps, 'onChange' | 'key'>
const inputProps = keys<InputProps>();

type InputChangeProps = React.ChangeEvent<HTMLTextAreaElement>;

type DefaultProps = FieldWrapperDefaultProps & {

    /**
     * The number of text rows to be displayed within the field.
     */
    height? : number;

    /**
     * The max number of text rows to be displayed within the field.
     * 0 means unlimited
     */
    maxHeight? : number;

    /**
     * The maximum image file size in bytes.
     */
    maxImageFileSize? : number;

    /**
     * The image width in pixel or -1 for no limitation.
     */
    imageWidth? : number;

    /**
     * The image height in pixel or -1 for no limitation.
     */
    imageHeight? : number;

    /**
     * Should the value shown as defined (raw) <strong>or</strong> should they be formatted.
     */
    rawMode? : boolean;

    /**
     * Should the field marked if dirty.
     */
    markDirty? : boolean;

    /**
     * The CSS class names to style the input field with.
     */
    inputClassNames? : string[];

    /**
     * The styles to style the input field with.
     */
    inputStyle? : React.CSSProperties;

    /**
     * true to use react-hook-form with debounced onChange event
     */
    useHookForm? : boolean;
};
const defaultProps : Required<DefaultProps> = {
    ...fieldWrapperDefaultProps,

    height          : 0,
    maxHeight       : 0,
    maxImageFileSize: 1000000,
    imageWidth      : -1,
    imageHeight     : -1,
    rawMode         : false,
    markDirty       : false,
    inputClassNames : [],
    inputStyle      : {},
    useHookForm     : false
};

type Props = Omit<FieldWrapperProps, 'pt'> & {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    /**
     * The id.
     */
    id? : string;

    /**
     * The name.
     */
    name? : string;

    /**
     * The value.
     */
    value? : string;

    /**
     * The value to determine dirty against.
     */
    originalValue? : string;

    /**
     * The placeholder.
     */
    placeholder? : string;

    tabIndex? : number;

    /**
     * Fired on change of input immediately if useHookForm is false, debounced if it's true
     * @param value
     * @param originalEvent
     */
    onChange : ( value : string | undefined, originalEvent : any ) => void;

    onBlur? : ( event : React.FocusEvent<Element> ) => void;

    onFocus? : ( event : React.FocusEvent<Element> ) => void;

    /**
     * Fired on change of input immediately if useHookForm is true otherwise never fired
     * @param value
     * @param originalEvent
     */
    onIntermediateChange? : ( value : string | undefined, originalEvent : any ) => void;

    pt? : {
        fieldWrapper? : FieldWrapperProps[ 'pt' ];
    };
};

export type ComponentProps = DefaultProps & Props;
export const componentProps = keys<ComponentProps>();

type AllProps = Required<DefaultProps> & Props;


const ImageDataField = ( props : AllProps ) => {
    // avoid rendering if hidden
    if ( props.visibility === 'hidden' ) {
        return null;
    }
    else {
        useSelector( ( state : StoreState ) => state.session?.user );

        const {
                  uid,
                  appendToElementId,
                  disabled,
                  height,
                  id,
                  imageHeight,
                  imageWidth,
                  infoTextLayout,
                  inputClassNames,
                  inputStyle,
                  labelPosition,
                  labelVisibility,
                  markDirty,
                  maxHeight,
                  maxImageFileSize,
                  messagePlaceholders,
                  messageTemplateStringResolver,
                  onBlur,
                  onChange,
                  onFocus,
                  onIntermediateChange,
                  originalValue,
                  placeholder,
                  pt,
                  rawMode,
                  tabIndex,
                  tooltipDisabled,
                  useHookForm,
                  value
              }                                                          = props,
              elementId                                                  = id ?? `${uid}_imageDataField`,
              inputRef                                                   = useRef<HTMLTextAreaElement>( null ),
              fileUploadRef                                              = useRef<FileUpload>( null ),
              overlayPanelRef                                            = useRef<OverlayPanel>( null ),
              overlayButtonRef                                           = useRef<Button>( null ),
              inValue                                                    = useRef<string | undefined>( value ),
              outValue                                                   = useRef<string | undefined>( value ),
              timeoutId                                                  = useRef<NodeJS.Timeout | undefined>(),
              showCodeCurrent                                            = useRef<boolean>( rawMode ),
              [ elementValue, setElementValue ]                          = useState<string | undefined>( value ),
              [ focus, setFocus ]                                        = useState<boolean>( false ),
              [ showCode, setShowCode ]                                  = useState<boolean>( rawMode ),
              [ appendTo, setAppendTo ]                                  = useState<'self' | HTMLElement>( 'self' ),
              [ forceRender, setForceRender ]                            = useState<number>( 0 ),
              [ overlay, setOverlay ]                                    = useState<boolean>( false ),
              heightStyle                                                = useMemo(
                  () => getHeightStyle( inputStyle, height, maxHeight, labelVisibility, labelPosition, infoTextLayout ),
                  [
                      height,
                      infoTextLayout,
                      inputStyle.height,
                      labelPosition,
                      labelVisibility,
                      maxHeight
                  ]
              ),
              messageResolver                                            = useMemo(
                  () => {
                      if ( !messageTemplateStringResolver ) {
                          return ( messageKey : string ) : string | undefined => messageKey === '' ? undefined : __( messageKey, messagePlaceholders, false );
                      }
                      else {
                          return ( messageKey : string ) => messageKey === '' ? undefined : __( messageTemplateStringResolver( __( messageKey, messagePlaceholders ) ) ?? '', undefined, false );
                      }
                  },
                  [
                      JSON.stringify( messagePlaceholders ),
                      messageTemplateStringResolver
                  ]
              ),
              resolvedPlaceholder                                        = useMemo(
                  () => {
                      return placeholder ? messageResolver( placeholder ) : undefined;
                  },
                  [
                      JSON.stringify( placeholder ),
                      messageResolver,
                      user ? user.language : undefined
                  ]
              ),
              onInputChange                                              = useCallback(
                  ( event : InputChangeProps ) => {
                      const changedValue = event.target.value;

                      if ( changedValue !== outValue.current ) {
                          setElementValue( changedValue );

                          onChange( changedValue, event );
                      }
                  },
                  [
                      onChange
                  ]
              ),
              onHookFormInputChange                                      = useCallback(
                  ( event : InputChangeProps ) => {
                      const changedValue = event.target.value;

                      if ( changedValue !== outValue.current ) {
                          setElementValue( changedValue );

                          onIntermediateChange?.( changedValue, event );

                          if ( timeoutId.current ) {
                              clearTimeout( timeoutId.current );
                          }
                          timeoutId.current = setTimeout( onChange.bind( null, changedValue, event ), useHookFormDelay );
                      }
                  },
                  [
                      onChange,
                      onIntermediateChange
                  ]
              ),
              onInputFocus                                               = useCallback(
                  ( event : React.FocusEvent<HTMLTextAreaElement> | React.FocusEvent<HTMLButtonElement> ) => {
                      setFocus( true );

                      onFocus?.( event );
                  },
                  [
                      onFocus
                  ]
              ),
              onInputBlur                                                = useCallback(
                  ( event : React.FocusEvent<HTMLTextAreaElement> ) => {
                      setFocus( false );

                      if ( timeoutId.current ) {
                          clearTimeout( timeoutId.current );
                      }

                      if ( outValue.current !== inValue.current ) {
                          onChange( outValue.current, event );
                      }

                      onBlur?.( event );
                  },
                  [
                      onBlur,
                      onChange
                  ]
              ),
              onButtonBlur                                               = useCallback(
                  ( event : React.FocusEvent<HTMLButtonElement> ) => {
                      setFocus( false );

                      onBlur?.( event );
                  },
                  [
                      onBlur
                  ]
              ),
              onDragOver                                                 = useCallback(
                  ( event : React.DragEvent<HTMLDivElement> ) => {
                      if ( !disabled ) {
                          if ( event.dataTransfer?.dropEffect ) {
                              event.dataTransfer.dropEffect = 'copy';
                          }
                          event.stopPropagation();
                          event.preventDefault();

                          setFocus( true );
                      }
                  },
                  [
                      disabled
                  ]
              ),
              onDragLeave                                                = useCallback(
                  ( event : React.DragEvent<HTMLDivElement> ) => {
                      if ( !disabled ) {
                          if ( event.dataTransfer?.dropEffect ) {
                              event.dataTransfer.dropEffect = 'none';
                          }

                          setFocus( false );
                      }
                  },
                  [
                      disabled
                  ]
              ),
              onShowUploadOverlayPanelClick                              = useCallback(
                  ( event : React.MouseEvent<HTMLButtonElement> ) => {
                      overlayPanelRef.current?.toggle( event );

                      // force rendering to align overlayPanel
                      setForceRender( forceRender + 1 );
                  },
                  [
                      forceRender
                  ]
              ),
              onShowCodeButtonClick                                      = useCallback(
                  () => {
                      setShowCode( !showCodeCurrent.current );
                  },
                  [
                      'onlyOnce'
                  ]
              ),
              onClearButtonClick                                         = useCallback(
                  ( event : React.MouseEvent<HTMLButtonElement> ) => {
                      const changedValue = undefined;

                      if ( changedValue !== outValue.current ) {
                          setElementValue( changedValue );

                          if ( !useHookForm ) {
                              onChange( changedValue, event );
                          }
                          else {
                              onIntermediateChange?.( changedValue, event );

                              if ( timeoutId.current ) {
                                  clearTimeout( timeoutId.current );
                              }
                              timeoutId.current = setTimeout( onChange.bind( null, changedValue, event ), useHookFormDelay );
                          }
                      }
                  },
                  [
                      onChange,
                      onIntermediateChange
                  ]
              ),
              onOverlayPanelShow                                         = useCallback(
                  () => {
                      setOverlay( true );
                  },
                  [
                      'onlyOnce'
                  ]
              ),
              onOverlayPanelHide                                         = useCallback(
                  () => {
                      invalidFiles = [];

                      setOverlay( false );
                  },
                  [
                      'onlyOnce'
                  ]
              ),
              onFileUploadSelect                                         = useCallback(
                  ( event : FileUploadSelectEvent | string ) => {
                      const handleFnc = async () => {
                          let src : string | undefined = undefined,
                              blob : Blob | undefined  = undefined,
                              file : File | undefined  = undefined;

                          if ( typeof event === 'string' ) {
                              if ( event.match( /^data:/ ) ) {
                                  src = event;
                              }
                              else {
                                  try {
                                      const fileResult = await fetch( event );

                                      blob = await fileResult.blob();
                                  }
                                  catch( e ) {
                                      console.error( e );
                                  }
                              }
                          }
                          else {
                              file = event.files[ 0 ];
                          }

                          if ( ( file && !invalidFiles.includes( file ) ) || src !== undefined || blob !== undefined ) {
                              const handleSetValue = ( changedValue : string ) => {
                                        overlayPanelRef.current?.hide();

                                        setElementValue( changedValue );

                                        if ( !useHookForm ) {
                                            onChange( changedValue, event );
                                        }
                                        else {
                                            onIntermediateChange?.( changedValue, event );

                                            if ( timeoutId.current ) {
                                                clearTimeout( timeoutId.current );
                                            }
                                            timeoutId.current = setTimeout( onChange.bind( null, changedValue, event ), useHookFormDelay );
                                        }
                                    },
                                    handleFileRead = () => {
                                        if ( src ) {
                                            handleSetValue( src );
                                        }
                                        else if ( file !== undefined || blob !== undefined ) {
                                            const reader = new FileReader();

                                            reader.onloadend = () => {
                                                handleSetValue( reader.result as string );
                                            };

                                            reader.readAsDataURL( ( file ?? blob ) as File | Blob );
                                        }
                                    };

                              // if to check image dimensions
                              if ( imageWidth !== -1 || imageHeight !== -1 ) {
                                  const uploadImage = new Image();

                                  uploadImage.onload = () => {
                                      // dimensions not matching
                                      if ( ( imageWidth !== -1 && uploadImage.width !== imageWidth ) || ( imageHeight !== -1 && uploadImage.height !== imageHeight ) ) {
                                          if ( fileUploadRef.current?.props.mode === 'advanced' ) {
                                              fileUploadRef.current?.messagesRef.current?.show( {
                                                  severity: 'error',
                                                  summary : __( 'YN.base.form.imageDataField.dimensions.invalid.summary', {
                                                      file: file ? file.name : 'dropped image'
                                                  } ),
                                                  detail  : parse( __( 'YN.base.form.imageDataField.dimensions.invalid.detail', {
                                                      width : imageWidth !== -1 ? `${imageWidth}${__( 'YN.base.form.imageDataField.dimensions.pixel' )}` : __( 'YN.base.form.imageDataField.dimensions.any' ),
                                                      height: imageHeight !== -1 ? `${imageHeight}${__( 'YN.base.form.imageDataField.dimensions.pixel' )}` : __( 'YN.base.form.imageDataField.dimensions.any' )
                                                  } ) )
                                              } );
                                          }

                                          // remove file
                                          fileUploadRef.current?.clear();
                                      }

                                      else {
                                          handleFileRead();
                                      }
                                  };

                                  uploadImage.src = src ? src : URL.createObjectURL( ( file ?? blob ) as File | Blob );
                              }

                              else {
                                  handleFileRead();
                              }
                          }
                      };

                      void handleFnc();
                  },
                  [
                      onChange,
                      onIntermediateChange
                  ]
              ),
              onFileUploadValidationFail                                 = useCallback(
                  ( file : File ) => {
                      invalidFiles.push( file );
                  },
                  [
                      'onlyOnce'
                  ]
              ),
              onDrop                                                     = useCallback(
                  ( event : React.DragEvent<HTMLDivElement> ) => {
                      if ( !disabled ) {
                          const html = event.dataTransfer.getData( 'text/html' );

                          event.stopPropagation();
                          event.preventDefault();

                          setFocus( false );

                          if ( html ) {
                              const regExp  = /<img[^>]+src="([^"]+)"[^>]*>/g,
                                    matches = regExp.exec( html );

                              if ( matches ) {
                                  onFileUploadSelect( matches[ 1 ] );
                              }
                          }
                          else {
                              fileUploadRef.current?.onFileSelect( event as unknown as FileUploadSelectEvent );
                          }
                      }
                  },
                  [
                      disabled
                  ]
              ),
              headerTemplate                                             = useCallback(
                  ( options : FileUploadHeaderTemplateOptions ) => {
                      const {
                                className: headerClassName,
                                chooseButton
                            }                                         = options,
                            restrictions : ( string | JSX.Element )[] = [];

                      if ( options.props.maxFileSize ) {
                          if ( restrictions.length ) {
                              restrictions.push( <br/> );
                          }
                          restrictions.push( __( 'YN.base.form.imageDataField.filesize.restriction', {
                              fileSize: formatBytes( maxImageFileSize, 2, 1000 )
                          } ) );
                      }

                      if ( imageWidth !== -1 || imageHeight !== -1 ) {
                          if ( restrictions.length ) {
                              restrictions.push( <br/> );
                          }
                          restrictions.push( __( 'YN.base.form.imageDataField.dimensions.restriction', {
                              width : imageWidth !== -1 ? `${imageWidth}${__( 'YN.base.form.imageDataField.dimensions.pixel' )}` : __( 'YN.base.form.imageDataField.dimensions.any' ),
                              height: imageHeight !== -1 ? `${imageHeight}${__( 'YN.base.form.imageDataField.dimensions.pixel' )}` : __( 'YN.base.form.imageDataField.dimensions.any' )
                          } ) );
                      }

                      return (
                          <div className={`${headerClassName} yn-panel-toolbar`}>
                              <div style={{ display: 'inline-block' }}>
                                  {chooseButton}
                              </div>
                              <div className="yn-fileupload-restrictions" style={{ float: 'right' }}>
                                  {restrictions}
                              </div>
                          </div>
                      );
                  },
                  [
                      imageHeight,
                      imageWidth,
                      maxImageFileSize
                  ]
              ),
              emptyTemplate                                              = useMemo(
                  () => <div className="yn-imagedata-empty flex align-items-center flex-column">
                      <i
                          className="pi pi-image mt-3 p-5"
                          style={{ fontSize: '4em', borderRadius: '50%', backgroundColor: 'var(--surface-b)', color: 'var(--surface-d)' }}
                      />
                      <span
                          className="my-5"
                          style={{ fontSize: '1.2em', color: 'var(--text-color-secondary)' }}
                      >
                      {__( 'YN.base.form.imageDataField.dragndrop.label' )}
                  </span>
                  </div>,
                  [
                      'onlyOnce'
                  ]
              ),
              image                                                      = useMemo(
                  () => {
                      let retVal : JSX.Element | undefined;

                      if ( !rawMode && elementValue ) {
                          retVal = imageRenderer( elementValue );
                      }
                      else {
                          retVal = <div className="yn-imagedata-empty">
                              <i className="pi pi-file"/>
                              <span>
                                  {__( 'YN.base.form.imageDataField.dragndrop.label' )}
                              </span>
                          </div>;
                      }

                      return retVal;
                  },
                  [
                      elementValue,
                      rawMode
                  ]
              ),
              fileUpload                                                 = useMemo(
                  () => {
                      return <FileUpload
                          key={`${elementId}_fileupload`}
                          id={`${elementId}_fileupload`}

                          ref={fileUploadRef}

                          name="imageDataFileupload"
                          multiple={false}
                          accept="image/*"
                          maxFileSize={maxImageFileSize}

                          onSelect={onFileUploadSelect}
                          onValidationFail={onFileUploadValidationFail}

                          headerTemplate={headerTemplate}
                          emptyTemplate={emptyTemplate}
                          chooseOptions={{ icon: 'pi pi-folder-open', iconOnly: true, className: 'p-button-text' }}

                          invalidFileAcceptMessageSummary={__( 'YN.base.form.imageDataField.accept.invalid.summary' )}
                          invalidFileAcceptMessageDetail={__( 'YN.base.form.imageDataField.accept.invalid.detail' )}
                          invalidFileSizeMessageSummary={__( 'YN.base.form.imageDataField.filesize.invalid.summary' )}
                          invalidFileSizeMessageDetail={__( 'YN.base.form.imageDataField.filesize.invalid.detail' )}
                      />
                  },
                  [
                      elementId,
                      maxImageFileSize,
                      onFileUploadSelect,
                      onFileUploadValidationFail,
                      headerTemplate,
                      emptyTemplate
                  ]
              ),
              [ bindOverlayClickListener, undbindOverlayClickListener ]  = useGlobalClickListener(
                  ( event : MouseEvent ) => {
                      if ( event.target && overlayPanelRef.current?.getElement()?.contains( event.target as Node ) ) {
                          const target = document.getElementById( elementId );

                          if ( target ) {
                              OverlayService.emit( 'overlay-click', {
                                  originalEvent: event as unknown as React.SyntheticEvent<Element, Event>,
                                  target
                              } );
                          }
                      }
                  } ),
              [ bindOverlayScrollListener, unbindOverlayScrollListener ] = useOverlayScrollListener( {
                  target  : inputRef.current as Ref<HTMLElement>,
                  listener: () => {
                      overlayPanelRef.current?.hide();
                  },
                  options : {
                      passive: true
                  },
                  when    : true
              } );

        let invalidFiles : File[] = [];

        inValue.current = value;
        outValue.current = elementValue;
        showCodeCurrent.current = showCode;

        useEffect(
            () => {
                if ( !focus ) {
                    setElementValue( value );
                }
            },
            [
                value
            ]
        );

        // on mount / unmount
        useEffect(
            () => {
                bindOverlayClickListener();

                // unmount
                return () => {
                    undbindOverlayClickListener();
                }
            },
            []
        );

        useEffect(
            () => {
                bindOverlayScrollListener();

                return () => {
                    unbindOverlayScrollListener();
                };
            },
            [
                bindOverlayScrollListener,
                unbindOverlayScrollListener
            ]
        );

        // resolve appendTo
        useLayoutEffect(
            () => {
                if ( appendToElementId && appendToElementId !== 'self' ) {
                    let appendToElement : HTMLElement | null;

                    if ( appendToElementId !== 'document' ) {
                        appendToElement = document.getElementById( appendToElementId );
                    }
                    else {
                        appendToElement = document.body;
                    }

                    if ( appendToElement && appendToElement !== appendTo ) {
                        setAppendTo( appendToElement );
                    }
                }
            }
        );

        // realign overlay
        useLayoutEffect(
            () => {
                let overlayElement : HTMLElement | undefined,
                    targetElement : HTMLElement | null | undefined;

                if ( overlayPanelRef.current?.getElement() ) {
                    overlayElement = overlayPanelRef.current?.getElement();
                    targetElement = document.getElementById( elementId );
                }

                if ( overlayElement && targetElement ) {
                    // wrong TypeScript definition for DomHandler.alignOverlay
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    DomHandler.alignOverlay( overlayElement, targetElement, appendTo ?? document.body, false );
                }
            }
        );

        return <FieldWrapper
            {..._.pick( props, fieldWrapperComponentProps ) as FieldWrapperComponentProps}

            uid={elementId}

            isDirty={markDirty && ( elementValue ?? '' ) !== ( originalValue ?? '' )}
            tooltipDisabled={tooltipDisabled || overlay}

            autoComplete={false}
            pt={pt?.fieldWrapper}
        >
            <div style={{ position: 'relative' }}>
                <div
                    id={elementId}
                    className={`yn-imagedata-field p-inputgroup yn-inputgroup yn-subfield${disabled ? ' p-disabled' : ''}${focus ? ' p-focus' : ''}${showCode ? ' yn-show-code' : ''}${elementValue ? '' : ' yn-imagedata-field-empty'}`}
                    style={{ ...defaultProps.inputStyle, ...inputStyle, ...heightStyle }}

                    onDragOver={onDragOver}
                    onDragLeave={onDragLeave}
                    onDrop={onDrop}

                    aria-describedby={`${elementId}_message`}
                >
                    <div className="yn-imagedata-image">
                        {image}
                    </div>
                    <InputTextarea
                        {..._.pick( props, inputProps ) as Omit<InputProps, 'onChange'>}

                        id={`${elementId}_field`}

                        ref={inputRef}

                        value={elementValue ? elementValue : ''}

                        onChange={useHookForm ? onHookFormInputChange : onInputChange}

                        className={`yn-imagedata-field ${inputClassNames.join( ' ' )}`}
                        style={{ ...defaultProps.inputStyle, ...inputStyle, width: 'auto' }}

                        placeholder={resolvedPlaceholder}
                        tooltip={undefined}
                        tooltipOptions={undefined}

                        tabIndex={( tabIndex ?? 0 )}
                        autoComplete="off"
                    />
                    <div className="yn-imagedata-button-wrapper">
                        <Button
                            className="yn-imagedata-button yn-imagedate-showupload-button"
                            icon="pi pi-image"
                            ref={overlayButtonRef}
                            onClick={onShowUploadOverlayPanelClick}

                            onFocus={onInputFocus}
                            onBlur={onButtonBlur}

                            tabIndex={( tabIndex ?? 0 ) + 1}
                        />
                        <Button
                            className={`yn-imagedata-button yn-imagedata-showcode-button ${rawMode ? 'hidden' : ''}`}
                            icon="pi pi-code"
                            onClick={onShowCodeButtonClick}

                            onFocus={onInputFocus}
                            onBlur={onButtonBlur}

                            tabIndex={( tabIndex ?? 0 ) + 2}
                        />
                        {elementValue &&
                            <Button
                                className={`yn-imagedata-button yn-imagedata-clear-button ${rawMode ? 'hidden' : ''}`}
                                icon="pi pi-times"
                                onClick={onClearButtonClick}

                                onFocus={onInputFocus}
                                onBlur={onButtonBlur}

                                tabIndex={( tabIndex ?? 0 ) + 3}
                            />
                        }
                    </div>
                </div>
                <OverlayPanel
                    id={`${elementId}_panel`}

                    ref={overlayPanelRef}

                    dismissable={true}

                    appendTo={appendTo}
                    className="yn-imagedataselector"

                    role="dialog"
                    onShow={onOverlayPanelShow}
                    onHide={onOverlayPanelHide}
                    /*pt={{
                        root: {
                            onMouseUp: onOverlayPanelClick.bind( null, 'mouseUp' )
                        }
                    }}*/
                >
                    {overlay &&
                        fileUpload
                    }
                </OverlayPanel>
                {!overlay &&
                    <div style={{ display: 'none' }}>
                        {fileUpload}
                    </div>
                }
            </div>
        </FieldWrapper>;
    }
};
ImageDataField.displayName = 'ImageDataField';
ImageDataField.defaultProps = defaultProps;

export default ImageDataField;
